import { Entry } from 'contentful'
import {
  useScreeningStore,
  useServicesStore,
  useUserStore,
  useHealthCallStore,
  useFeatureFlagsStore,
  useCertificationStore,
  useScreeningQuestionnaireStore,
} from './store'
import {
  IPortalGeneralPageFields,
  LANGUAGE_TO_LOCALE_MAP,
  ScreeningStatus,
  ScreeningType,
  Services,
  SupportedLanguage,
} from '@estercare/ester-shared'
import { isCertificationService, isServiceIdentifier } from '../utils/typeGuards'
import { CERTIFICATE_POLICY_QUESTION_ID } from '../api/ester-b2b-api/private/certification/types'
import { Permission } from '../api/ester-b2b-api/private/users/types'
import { useShallow } from 'zustand/react/shallow'

export const useSelectIsAdmin = () => useUserStore((state) => state.user?.permissions.includes('ADMIN'))
export const useSelectIsAthlete = () => useUserStore((state) => state.user?.permissions.includes('ATHLETE'))
export const useSelectUserId = () => useUserStore((state) => state.user?.id)
export const useSelectUserFullName = () =>
  useUserStore(
    useShallow((state) =>
      state.user?.firstName || state.user?.lastName ? `${state.user?.firstName} ${state.user?.lastName}` : undefined
    )
  )
export const useSelectFirstName = () => useUserStore((state) => state.user?.firstName)
export const useSelectLastName = () => useUserStore((state) => state.user?.lastName)
export const useSelectHasPersonalNumber = () => useUserStore((state) => !!state.user?.personalNumber)
export const useSelectPersonalNumber = () => useUserStore((state) => state.user?.personalNumber)
export const useSelectUserEmail = () => useUserStore((state) => state.user?.email)
export const useSelectUserOrganisationId = () => useUserStore((state) => state.user?.organisationId)
export const useSelectUserOrganisationName = () => useUserStore((state) => state.user?.organisationName)
export const useIsUserSet = () => useUserStore((state) => !!state.user)

export const useSelectActiveServices = () => useServicesStore(useShallow((state) => state.activeServices))
export const useSelectUserGender = () => useUserStore((state) => state.user?.gender)
export const useSelectUserZipcode = () => useUserStore((state) => state.user?.zipCode)
export const useSelectUserBirthYear = () => useUserStore((state) => state.user?.birthYear)
export const useSelectUserIsManager = () => useUserStore((state) => state.user?.isManager)
export const useSelectUserLanguage = () => useUserStore((state) => state.user?.language)
export const useSelectUserMarket = () => useUserStore((state) => state.user?.market)
export const useSelectIsUserSso = () => useUserStore((state) => state.user?.organisationName === 'Nordea') // TODO, make correct check when SSO has some kind of identifier on users
export const useSelectUserIsInvited = () =>
  useUserStore((state) => state.user?.inviterName !== null && state.user?.inviterName !== undefined)
export const useSelectUserInviterName = () => useUserStore((state) => state.user?.inviterName)
export const useSelectDataPolicyAcknowledgementRequired = () =>
  useUserStore((state) => !!state.user?.dataPolicy?.acknowledgementRequired)

export const useSelectUserLocale = () => {
  const userLanguage = useSelectUserLanguage() || SupportedLanguage.SWEDISH
  return LANGUAGE_TO_LOCALE_MAP[userLanguage]
}

export const useSelectScreeningStatus = (): ScreeningStatus | undefined => {
  return useScreeningStore((state) => state.screeningData?.screeningStatus)
}

export const useSelectScreenings = () => {
  return useScreeningStore((state) => state.screeningData?.screenings)
}

export const useSelectFormattedScreeningHandledDate = () => {
  const screenings = useSelectScreenings()
  const userLocale = useSelectUserLocale()

  const latestStandard = screenings?.find((s) => s.type === ScreeningType.STANDARD)
  if (!latestStandard?.handledAt) {
    return null
  }

  return new Date(latestStandard.handledAt).toLocaleString(userLocale, { month: 'long', year: 'numeric' })
}

// Backend returns screenings sorted by createdAt desc — screenings[0] is the most recent.
export const useSelectScreeningId = () => {
  const screenings = useSelectScreenings()

  const latestScreening = screenings?.[0]
  const latestFollowUp = screenings?.find((s) => s.type === ScreeningType.FOLLOW_UP)
  const latestStandard = screenings?.find((s) => s.type === ScreeningType.STANDARD)

  return {
    standardScreeningId: latestStandard?.id,
    followUpScreeningId: latestFollowUp?.id,
    latestScreeningId: latestScreening?.id,
  }
}

// Returns the type of the most recent screening, or undefined if no screenings exist.
// Relies on backend returning screenings sorted by createdAt desc.
export const useSelectLatestScreeningType = (): ScreeningType | undefined => {
  const screenings = useSelectScreenings()
  const latestScreening = screenings?.[0]
  const typeMap: Partial<Record<ScreeningType, ScreeningType>> = {
    [ScreeningType.FOLLOW_UP]: ScreeningType.FOLLOW_UP,
    [ScreeningType.CHECK_IN]: ScreeningType.CHECK_IN,
  }
  return latestScreening ? (typeMap[latestScreening.type] ?? ScreeningType.STANDARD) : undefined
}

export const useSelectIsScreeningVersion1 = (): boolean => {
  const screenings = useSelectScreenings()
  if (!screenings) return false

  const standardScreenings = screenings.filter((s) => s.type === ScreeningType.STANDARD)
  const recentScreenings = standardScreenings.slice(0, 2)

  return recentScreenings.every((s) => (s.screeningResultVersion ?? 1) === 1)
}

export const useSelectActiveServicesFromContentful = (
  ServiceIndex: Entry<IPortalGeneralPageFields>[],
  userRole: Permission
) => {
  return useServicesStore(
    useShallow((state) => {
      const { activeServices } = state

      return ServiceIndex.filter((service) => {
        const { serviceIdentifier, role } = service.fields

        if (!isServiceIdentifier(serviceIdentifier)) {
          return false
        }
        if (userRole !== role) {
          return false
        }

        return activeServices.includes(serviceIdentifier as Services)
      })
    })
  )
}

export const useSelectInactiveServicesFromContentful = (
  ServiceIndex: Entry<IPortalGeneralPageFields>[],
  userRole: Permission
) => {
  return useServicesStore(
    useShallow((state) => {
      const { activeServices } = state

      return ServiceIndex.filter((service) => {
        const { serviceIdentifier, role } = service.fields

        if (!isServiceIdentifier(serviceIdentifier) || userRole !== role) {
          return false
        }
        //Only show certification if it is activated
        if (isCertificationService(serviceIdentifier)) {
          return false
        }

        return !activeServices.includes(serviceIdentifier as Services)
      })
    })
  )
}

export const selectLatestScreeningDateByType = (
  screenings: ReturnType<typeof useSelectScreenings>,
  type: ScreeningType
): string | null => {
  if (!screenings) return null
  const filtered = screenings.filter((s) => s.type === type)
  if (filtered.length === 0) return null
  const latest = filtered[0]
  return latest?.createdAt || null
}
export const useSelectCertificationScreeningId = () => useCertificationStore((state) => state.screeningId)
export const useSelectCertificationPolicyAnswer = () =>
  useScreeningQuestionnaireStore((state) => state.answers[CERTIFICATE_POLICY_QUESTION_ID]?.[0])
export const useSelectCertificationPolicyDocuments = () => useCertificationStore((state) => state.policyDocuments)

export const useSelectBookingPersonalNumber = () => useHealthCallStore((state) => state.bookingFormData?.personalNumber)
export const useSelectBookingPhoneNumber = () => useHealthCallStore((state) => state.bookingFormData?.phoneNumber)
export const useSelectBookingFormData = () =>
  useHealthCallStore(
    useShallow((state) => ({
      personalNumber: state.bookingFormData?.personalNumber || '',
      phoneNumber: state.bookingFormData?.phoneNumber || '',
    }))
  )

export const useSelectFeatureFlags = () => useFeatureFlagsStore((state) => state.featureFlags)
export const useIsFeatureFlagsLoaded = () => useFeatureFlagsStore((state) => state.isLoaded)
export const useIsFeatureEnabled = (flagId: string) =>
  useFeatureFlagsStore((state) => state.featureFlags?.[flagId]?.toLowerCase() === 'true')

export const useIsAnyScreeningHandled = () => {
  const screenings = useSelectScreenings()
  if (!screenings) return false
  return screenings.some((s) => s.completed && s.handledAt !== null)
}

const SCREENING_DUE_STATUSES: ScreeningStatus[] = [
  ScreeningStatus.NOT_STARTED,
  ScreeningStatus.INCOMPLETE,
  ScreeningStatus.RENEW,
  ScreeningStatus.FOLLOW_UP_NOT_STARTED,
  ScreeningStatus.FOLLOW_UP_INCOMPLETE,
  ScreeningStatus.CHECK_IN_NOT_STARTED,
  ScreeningStatus.CHECK_IN_INCOMPLETE,
]

export const useIsScreeningDue = () => {
  const screeningStatus = useSelectScreeningStatus()
  if (!screeningStatus) return false
  return SCREENING_DUE_STATUSES.includes(screeningStatus)
}
